/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import useConfig from "../../../../actions/Tenants/config/configHook";
import { useFields } from "../../../../hooks/useFields";
import { getFilledArrayOrDefault } from "../../../../utils";
import { dayjs } from "../../../../utils/date";
import { ClassDistribution } from "../Helpers/ClassDistribution";
import { RegisterData, RegisterDataProps } from "../Helpers/RegisterData";
import { getRipeningClasses, RipeningDenominator } from "../hooks/getRipeningClasses";
import useRipeningRoom, { DATE_URL_FORMAT, formatDate } from "../hooks/useRipeningRoom";

export function RipeningDayRoom({
    selected,
    date,
    pallets,
    ripeningField,
    registerData,
    checkSessions,
    checks
}: {
    selected: boolean;
    date: Dayjs;
    pallets: any[];
    ripeningField: string;
    registerData?: RegisterDataProps;
    checkSessions?: any[];
    checks?: any[];
}) {
    const config = useConfig();
    const { fruit_type_config } = useRipeningRoom();
    const [showDistributionModal, setShowDistributionModal] = useState(false);
    const glowingEffect = selected ? "box-shadow: 0px 0px 8px 8px rgba(19, 165, 95, 0.1);" : "";

    const titleBackgroundClass = selected ? "text-white" : "bg-light";
    const [showPercent, setShowPercent] = useState(false);
    const denominator = config.root_config?.ripening_room_denominator_for_relative_classes || RipeningDenominator.checked_pallets;

    const totalPallets = getFilledArrayOrDefault(pallets).length;
    const { classes, total, checked } = getRipeningClasses({
        pallets,
        ripeningField,
        date,
        denominator
    });
    const ripeningFieldDistributions = (fruit_type_config?.ripening_fields || []).map((field) => ({
        field,
        ...getRipeningClasses({
            pallets,
            ripeningField: field,
            date,
            denominator,
        })
    }));

    // * Compute checks that happened on this day for pallets in this cell. This is the same
    // * signal the per-day column in the pallet table uses, so the day-card and the pallet
    // * table stay consistent (EFQ-4171).
    const palletIdsInCell = new Set(getFilledArrayOrDefault(pallets).map((p) => String(p.id)));
    const checksForDate = getFilledArrayOrDefault(checks)
        .filter((c) => dayjs(c.created).format(DATE_URL_FORMAT) === date.format(DATE_URL_FORMAT))
        .filter((c) => getFilledArrayOrDefault(c.layer_ids).some((id) => palletIdsInCell.has(String(id))));
    const latestCheckForDate = checksForDate.length > 0
        ? checksForDate.reduce((a, b) => (dayjs(a.created).isAfter(dayjs(b.created)) ? a : b))
        : null;
    const checkedCount = checksForDate.length > 0
        ? getFilledArrayOrDefault(pallets).reduce((count, pallet) => {
            const palletId = String(pallet.id);
            const hasCheckOnDate = checksForDate.some((check) => getFilledArrayOrDefault(check.layer_ids)
                .some((id) => String(id) === palletId));
            return hasCheckOnDate ? count + 1 : count;
        }, 0)
        : checked;

    // Get the latest check session data for this day (preferred for aggregate meta like inspected_by/last_check)
    const latestCheckSession = getLatestCheckSession(checkSessions, date);

    return (
        <>
            <div
                className="cursor-pointer rounded bg-white"
                css={[css`${glowingEffect}`, css`width: 280px;box-shadow: 0px 0px 5px rgba(19, 165, 95, 0.2);`]}
            >
                <div
                    className={`p-2 rounded-top text-center ${titleBackgroundClass}`}
                    css={css`background-color: ${selected ? "#13A55F" : "#f8f9fa"};`}
                >
                    <span>{formatDate(date)}</span>
                </div>

                <div
                    className="mb-1 font-weight-bold p-2 pt-3"
                    css={css` font-size: 14px; `}
                >
                    <div onClick={() => setShowDistributionModal(true)} css={css`min-height: 100px;`}>
                        <LatestCheckDisplay
                            latestCheckSession={latestCheckSession}
                            latestCheck={latestCheckForDate}
                            totalPallets={totalPallets}
                            checked={checkedCount}
                        />
                    </div>

                    {registerData && (
                        <div className="px-2">
                            <div className="my-3 border-top"></div>
                            <RegisterData
                                isToday={date.isSame(dayjs(), "day")}
                                registerData={registerData}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Modal isOpen={showDistributionModal} toggle={() => setShowDistributionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowDistributionModal(false)}>
                    Class Distribution - {formatDate(date)}
                </ModalHeader>
                <ModalBody>
                    <div className="cursor-pointer" onClick={() => setShowPercent((prev) => !prev)}>
                        {ripeningFieldDistributions.length > 0 ? ripeningFieldDistributions.map((distribution, index) => (
                            <div key={distribution.field?.fieldname_getter || index} className={index > 0 ? "mt-4 pt-3 border-top" : ""}>
                                <div className="fw-semibold mb-2">{distribution.field?.label || distribution.field?.fieldname_getter || "Ripening class"}</div>
                                <ClassDistribution
                                    classes={distribution.classes}
                                    total={distribution.total}
                                    totalPallets={totalPallets}
                                    denominator={denominator}
                                    showPercent={showPercent}
                                    hideNumberOfCheckedPallets={false}
                                />
                            </div>
                        )) : (
                            <ClassDistribution
                                classes={classes}
                                total={total}
                                totalPallets={totalPallets}
                                denominator={denominator}
                                showPercent={showPercent}
                                hideNumberOfCheckedPallets={false}
                            />
                        )}
                    </div>
                </ModalBody>
            </Modal>
        </>
    );
}

function LatestCheckDisplay({ latestCheckSession, latestCheck, totalPallets, checked }) {
    const config = useConfig();
    const { fruit_type, fruit_type_config } = useRipeningRoom();

    const { setupFieldClassFromConfig } = useFields();
    const ripeningFields = (fruit_type_config?.ripening_fields || [])
        .map((fieldConfig) => setupFieldClassFromConfig(fieldConfig, { fruit_type }))
        .filter((field) => field);

    // * Prefer the check session for aggregate meta (inspected_by, last_check) since
    // * a session groups related checks; fall back to the latest check on this day so
    // * the card is not blank when a session record is missing (EFQ-4171).
    const source = latestCheckSession || latestCheck;

    if (!source) {
        return (
            <div className="text-center text-muted">
                <div className="mb-2">No checks</div>
                <div className="small">Total pallets {totalPallets}</div>
            </div>
        );
    }

    const inspectedBy = latestCheckSession?.inspected_by || latestCheck?.inspected_by;
    const lastCheckAt = latestCheckSession?.last_check || latestCheck?.created;

    return (
        <table className="w-100">
            <tbody>

                {ripeningFields.filter((i) => i.fieldname_getter !== "label").map((i, index) => <tr key={index}>
                    <td>{i.label}</td>
                    <td className="fw-bold ps-2 text-end">
                        {
                            i.set_value({
                                config,
                                layer: source,
                                check: source, // * To be used in check tables and layer overview
                                fruit: null, // * Fruit list is not available on overview screen
                                children: null, // * children is only available on layer index page
                            // form_options: config.form_options
                            }).display_component()
                        }
                    </td>
                </tr>)}

                <tr>
                    <td className="pt-2">Inspected by</td>
                    <td className="fw-bold ps-2 text-end pt-2">
                        {inspectedBy}
                    </td>
                </tr>
                <tr>
                    <td>Last check</td>
                    <td className="fw-bold ps-2 text-end">
                        {lastCheckAt ? dayjs.utc(lastCheckAt).local().format("HH:mm") : "-"}
                    </td>
                </tr>
                <tr>
                    <td>Checked</td>
                    <td className="fw-bold ps-2 text-end">
                        {checked}/{totalPallets}
                    </td>
                </tr>
            </tbody>
        </table>
    );
}

function getLatestCheckSession(checkSessions, date) {
    if (!checkSessions || checkSessions.length === 0) {
        return null;
    }

    // Filter check sessions for the specific date
    const sessionsForDate = checkSessions.filter((session) => dayjs(session.created).isSame(date, "day"));

    if (sessionsForDate.length === 0) {
        return null;
    }

    // Sort by creation time and return the latest
    const sortedSessions = sessionsForDate.sort((a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf());

    return sortedSessions[0];
}
